import { createSelector } from '@reduxjs/toolkit';
import uniqBy from 'lodash/uniqBy';
import initialState from 'state/initial-state';
import type { RootState } from 'state/modules';

// Denormalized API blob whose view type each consumer declares for itself.
type Product = any;

type ProductId = string | number;

/**
 * Select all lenders from the state.
 * @param  {Object} state
 * @return {Array}
 */
const getAllLenders = () => initialState.lendersById || {};

/**
 * Select all the products from products.
 * @param {Object} state
 * @return {Object}
 */
const getAllProducts = (state: RootState): Record<string, Product> => state.products.byId;

/**
 * Get the list of product ids from the current search results.
 * @param  {Object} state
 * @return {Array}
 */
const getAllResults = (state: RootState): ProductId[] => state.products.results;

/**
 * Get compared product ids.
 * @param {*} state
 * @return {Array}
 */
const getAllCompared = (state: RootState): ProductId[] => state.products.compared;

/**
 * Get a Product from state by Id.
 * @param  {Object} state
 * @param  {Number} productId
 * @return {Object}
 */
const getProductById = (state: RootState, productId: ProductId) => getAllProducts(state)[productId];

/**
 * Denormalize Product
 * @param {Object} product
 * @param {Array} lenders
 * @return {Object}
 */
const denormalizeProduct = (product: Product, lenders: Record<string, Product>) => {
  if (!product) return null;
  const clone = { ...product };
  clone.lender = { ...(lenders[clone.lender_id] || {}), ...(product.lender || {}) };
  clone.rate_hold_int = parseInt(clone.rate_hold, 10);
  return clone;
};

/**
 * Get the current fetch meta.
 * @param  {Object} state
 * @return {Object}
 */
export const getMeta = (state: RootState) => state.products.meta;

/**
 * Get the current loading state.
 * @param {Object} state
 * @return {Boolean}
 */
export const isLoading = (state: RootState) => state.products.pendingRequestsCount !== 0;

/**
 * Get the last fetch error.
 * @param {Object} state
 * @return {Object|null}
 */
export const getError = (state: RootState) => state.products.error;

/**
 * Get Product by Id.
 * @param  {Object} state
 * @param  {Number} productId
 * @return {Object}
 */
export const getById = createSelector([getProductById, getAllLenders], (product, lenders) =>
  denormalizeProduct(product, lenders)
);

/**
 * Get all the products from the current search.
 * @return {Function}
 */
export const getProducts = createSelector(
  [getAllResults, getAllProducts, getAllLenders],
  (ids: (number | string)[], products, lenders) =>
    uniqBy(
      ids.map((productId) => denormalizeProduct(products[productId], lenders)),
      'id'
    )
);

/**
 * Get all compared products with lenders.
 */
export const getComparedProducts = createSelector(
  [getAllCompared, getAllProducts, getAllLenders],
  (ids, products, lenders) =>
    ids.reduce<(Product | null)[]>((acc, productId) => {
      const product = products[productId];
      return product ? [...acc, denormalizeProduct(product, lenders)] : acc;
    }, [])
);

/**
 * Get the number of compared products.
 * @param {*} state
 * @return {Number}
 */
export const getComparedProductsCount = (state: RootState) => getComparedProducts(state).length;

/**
 * Gets the number of compared rates that have been removed.
 * @param {*} state
 * @return {Number}
 */
export const getStaleComparedProductsCount = (state: RootState) => state.products.staleCompared;

/**
 * Returns true if the compare sidebar is open.
 * @param {*} state
 * @return {Boolean}
 */
export const isCompareVisible = (state: RootState) => state.products.compareVisible;
